/**
 * Funções puras de busca/filtros da Home (rule `pages.md`/`testing.md`).
 *
 * Camada de tradução do formulário da Home para o contrato de requisição do
 * BBMNET-LICITACOES-BACKEND na rota EXCLUSIVA do jornal
 * `GET api/EditaisInternal/Jornal/Editais` (`EditalJornalFiltersDto` — ADR-010).
 *
 * Contrato de query (PascalCase, exatamente como o .NET espera):
 *   Skip, Take, Uf, ModalidadeId, NumeroEdital, ObjetoId, SegmentoUniqueId,
 *   OrgaoPromotorId, Situacao, PalavraChave.
 *
 * Mapeamento (ADR-004):
 *   estado           → Uf (string)
 *   modalidade       → ModalidadeId (int; omitido se não for int válido)
 *   numeroEdital     → NumeroEdital (string)
 *   objeto           → ObjetoId (Guid)
 *   segmento         → SegmentoUniqueId (Guid; atrás de feature flag)
 *   orgao (nome)     → OrgaoPromotorId (Guid; resolvido por `selectedOrgao`)
 *   situacao (rótulo)→ Situacao (int; lista estável `StatusSalaDisputa`)
 *   palavraChave     → PalavraChave (texto livre cross-field; atrás de flag)
 *   cidade           → ADIADA (ADR-007): oculta na UI, NUNCA enviada.
 */

import type { OrgaoOption } from '@libs/types';

/**
 * Estado do formulário de busca da Home. Campos textuais; `modalidade`/`situacao`
 * carregam o `int` (como string ou number) escolhido no dropdown.
 */
export type SearchState = {
  numeroEdital: string;
  estado: string;
  /** `int` da modalidade (ModalidadeId). '' quando nada selecionado. */
  modalidade: string;
  /** Nome digitado/selecionado no autocomplete — nunca enviado cru como id. */
  orgao: string;
  /** Guid resolvido do órgão (OrgaoPromotorId), via `selectedOrgao`. */
  orgaoPromotorId: string;
  /** `int` da situação (Situacao), da lista estável `StatusSalaDisputa`. */
  situacao: string;
  palavraChave: string;
  /** Guid do objeto (ObjetoId). */
  objeto: string;
  /** Guid do segmento (SegmentoUniqueId). */
  segmento: string;
  /** Adiada (ADR-007): mantida no estado, mas nunca enviada ao LICITACOES. */
  cidade: string;
};

/** Paginação server-side (offset/tamanho). */
export type QueryPage = { skip: number; take: number };

/**
 * Flags por filtro dependente do backend (task_01). Default habilitado — só
 * `false` explícito desliga o filtro (não exibe/não envia).
 */
export type QueryFeatureFlags = {
  palavraChave?: boolean;
  segmento?: boolean;
};

/** Regra de tamanho da palavra-chave (mínimo/truncamento). */
export const MIN_PALAVRA_CHAVE = 2;
export const MAX_PALAVRA_CHAVE = 100;

/**
 * Lista estável de situação espelhando o enum `StatusSalaDisputa` do LICITACOES
 * (ADR-007 / TechSpec §Data Models). Fonte no cliente: sem endpoint interno.
 * Se `StatusSalaDisputa` mudar no .NET, atualizar aqui (coberto por teste).
 */
export const SITUACAO_OPTIONS: { label: string; value: number }[] = [
  { label: 'Não Publicado', value: 1 },
  { label: 'Não iniciado', value: 2 },
  { label: 'Em execução', value: 3 },
  { label: 'Encerrado', value: 4 },
  { label: 'Revogado', value: 5 },
  { label: 'Anulado', value: 6 },
  { label: 'Suspenso', value: 7 },
];

// ─── Helpers internos ─────────────────────────────────────────────────────────

/** Retorna a string aparada, ou '' para valor ausente/não-string. */
const trimOr = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

/**
 * Converte um valor em `int` seguro, ou `undefined` quando não é um inteiro
 * confiável (evita enviar parâmetro malformado — ex.: `ModalidadeId=abc`).
 */
const toInt = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number(String(value).trim());
  return Number.isInteger(n) ? n : undefined;
};

/**
 * Normaliza a palavra-chave: apara, aplica tamanho mínimo (omite se menor) e
 * trunca no máximo. Não faz encoding aqui — quem monta a query encoda.
 */
const normalizePalavraChave = (value: unknown): string => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (trimmed.length < MIN_PALAVRA_CHAVE) return '';
  return trimmed.slice(0, MAX_PALAVRA_CHAVE);
};

// ─── Situação (dropdown estável) ──────────────────────────────────────────────

/**
 * Opções de situação oferecidas ao dropdown, filtrando qualquer entrada sem
 * `int` confiável (não oferece rótulo que não mapeia para `Situacao` — UT-072).
 * Só é oferecida quando há fonte estável presente (UT-071).
 */
export function buildSituacaoOptions(
  source: { label: string; value: unknown }[] = SITUACAO_OPTIONS,
): { label: string; value: number }[] {
  return (source ?? [])
    .filter(
      (o) =>
        o &&
        typeof o.label === 'string' &&
        o.label.trim() !== '' &&
        Number.isInteger(o.value),
    )
    .map((o) => ({ label: o.label, value: o.value as number }));
}

/**
 * Resolve o `int` (Situacao) a partir do rótulo escolhido. Retorna `undefined`
 * para rótulo sem correspondência confiável (UT-070/UT-072).
 */
export function situacaoIntFromLabel(
  label: string,
  source: { label: string; value: unknown }[] = SITUACAO_OPTIONS,
): number | undefined {
  const match = buildSituacaoOptions(source).find((o) => o.label === label);
  return match?.value;
}

// ─── Órgão (autocomplete + resolução nome→id) ─────────────────────────────────

/**
 * Normaliza o resultado de `getOrgaosPromotores` em sugestões de autocomplete,
 * deduplicando por rótulo (via `Set`) e descartando entradas inválidas
 * (sem `label`, tipo errado). Termo sem correspondência → `[]` (UT-062/UT-064).
 */
export function buildOrgaoSuggestions(rawItems: unknown[]): OrgaoOption[] {
  const seen = new Set<string>();
  const out: OrgaoOption[] = [];

  for (const item of rawItems ?? []) {
    if (!item || typeof item !== 'object') continue;
    const opt = item as Partial<OrgaoOption>;
    const label = typeof opt.label === 'string' ? opt.label.trim() : '';
    if (!label || seen.has(label)) continue;
    seen.add(label);
    out.push({
      label,
      value: typeof opt.value === 'string' ? opt.value : '',
    });
  }

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Resolve o nome selecionado do órgão para `OrgaoPromotorId` (Guid), casando
 * pelo rótulo EXATO contra a lista carregada (UT-060/UT-061). Texto digitado
 * sem correspondência exata NÃO vira id (nunca envia texto cru — UT-063).
 */
export function selectedOrgao(
  state: SearchState,
  orgaos: OrgaoOption[],
): { orgaoPromotorId?: string } {
  const name = trimOr(state?.orgao);
  if (!name) return {};

  const match = (orgaos ?? []).find(
    (o) => o && typeof o.label === 'string' && o.label.trim() === name,
  );

  const id = match ? trimOr(match.value) : '';
  return id ? { orgaoPromotorId: id } : {};
}

// ─── Montagem da query string ─────────────────────────────────────────────────

/**
 * Monta a query string do jornal (`?Skip=...&Take=...&...`) a partir do estado
 * de busca, com paginação server-side e o mapeamento direto dos filtros. Filtros
 * vazios são omitidos; nenhum parâmetro malformado é enviado. `cidade` nunca é
 * enviada (adiada — ADR-007). `palavraChave`/`segmento` respeitam a feature flag
 * (default habilitado; `false` explícito omite). Persiste todos os filtros ao
 * paginar (só `Skip` muda).
 */
export function buildEditaisQueryString(
  state: SearchState,
  page: QueryPage,
  flags: QueryFeatureFlags = {},
): string {
  const params: string[] = [];
  const push = (key: string, value: string) =>
    params.push(`${key}=${encodeURIComponent(value)}`);

  const skip = Number.isFinite(page?.skip) ? Number(page.skip) : 0;
  const take = Number.isFinite(page?.take) ? Number(page.take) : 10;
  params.push(`Skip=${skip}`);
  params.push(`Take=${take}`);

  const uf = trimOr(state?.estado);
  if (uf) push('Uf', uf);

  const modalidadeId = toInt(state?.modalidade);
  if (modalidadeId !== undefined) params.push(`ModalidadeId=${modalidadeId}`);

  const numero = trimOr(state?.numeroEdital);
  if (numero) push('NumeroEdital', numero);

  const objetoId = trimOr(state?.objeto);
  if (objetoId) push('ObjetoId', objetoId);

  if (flags.segmento !== false) {
    const segmento = trimOr(state?.segmento);
    if (segmento) push('SegmentoUniqueId', segmento);
  }

  const orgaoPromotorId = trimOr(state?.orgaoPromotorId);
  if (orgaoPromotorId) push('OrgaoPromotorId', orgaoPromotorId);

  const situacao = toInt(state?.situacao);
  if (situacao !== undefined) params.push(`Situacao=${situacao}`);

  if (flags.palavraChave !== false) {
    const palavraChave = normalizePalavraChave(state?.palavraChave);
    if (palavraChave) push('PalavraChave', palavraChave);
  }

  // cidade: adiada (ADR-007) — controle oculto e nenhum parâmetro enviado.

  return `?${params.join('&')}`;
}
